import { useEffect, useRef, useState } from "react";
import { canManageCourtPricing } from "../lib/access";
import { ApiError } from "../lib/api/client";
import { getTenantFinancialSettings, updateTenantFinancialSettings } from "../lib/api/endpoints";
import { parseBrlAmount } from "../lib/financialSettingsValidation";
import { useSession } from "../lib/session/SessionContext";
import { C } from "../theme";
import type { TenantFinancialSettingsDto } from "../types";
import { Button } from "./Button";
import { Card } from "./Card";
import { FieldInput } from "./Field";
import { Label } from "./Label";

function formatBrlInput(amount: number | null): string {
  return amount === null ? "" : amount.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.detail ?? error.message : fallback;
}

export function TenantFinancialSettingsTab() {
  const { accessToken, activeTenant } = useSession();
  const token = accessToken ?? "";
  const canManage = canManageCourtPricing(activeTenant?.roleSlug ?? null);
  const savingRef = useRef(false);
  const [settings, setSettings] = useState<TenantFinancialSettingsDto | null>(null);
  const [totalPassAmount, setTotalPassAmount] = useState("");
  const [wellhubAmount, setWellhubAmount] = useState("");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!token || !canManage) return;
    let cancelled = false;
    setSettings(null);
    setLoadError(null);
    getTenantFinancialSettings(token).then(result => {
      if (cancelled) return;
      setSettings(result);
      setTotalPassAmount(formatBrlInput(result.totalPassAmount));
      setWellhubAmount(formatBrlInput(result.wellhubAmount));
    }).catch(error => {
      if (!cancelled) setLoadError(errorMessage(error, "Falha ao carregar configurações financeiras"));
    });
    return () => { cancelled = true; };
  }, [token, canManage, reloadKey]);

  const save = async () => {
    if (!token || savingRef.current) return;
    const totalPass = parseBrlAmount(totalPassAmount);
    const wellhub = parseBrlAmount(wellhubAmount);
    if (totalPass === undefined || wellhub === undefined) {
      setSaveError("Informe um valor em BRL válido, não negativo e com até duas casas decimais.");
      setSuccess(null);
      return;
    }

    savingRef.current = true;
    setSaving(true);
    setSaveError(null);
    setSuccess(null);
    try {
      const saved = await updateTenantFinancialSettings(token, { totalPassAmount: totalPass, wellhubAmount: wellhub });
      setSettings(saved);
      setTotalPassAmount(formatBrlInput(saved.totalPassAmount));
      setWellhubAmount(formatBrlInput(saved.wellhubAmount));
      setSuccess("Configurações financeiras atualizadas com sucesso.");
    } catch (error) {
      setSaveError(errorMessage(error, "Falha ao salvar configurações financeiras"));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  if (!canManage) return null;

  return (
    <Card>
      <Label>Configurações financeiras</Label>
      <p style={{ color: C.light, fontSize: 12.5, margin: "4px 0 16px" }}>Defina os valores de repasse em BRL para o tenant ativo.</p>
      {loadError ? (
        <div role="alert" style={{ color: C.red, fontSize: 12.5 }}>
          {loadError} <Button variant="ghost" onClick={() => setReloadKey(value => value + 1)}>Tentar novamente</Button>
        </div>
      ) : settings === null ? (
        <div style={{ color: C.light, fontSize: 13 }}>Carregando configurações financeiras...</div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
            <div>
              <label htmlFor="totalpass-amount" style={{ color: C.gray, display: "block", fontSize: 12, marginBottom: 5 }}>Valor do TotalPass</label>
              <FieldInput id="totalpass-amount" aria-label="Valor do TotalPass" inputMode="decimal" placeholder="0,00" value={totalPassAmount} onChange={event => { setTotalPassAmount(event.target.value); setSaveError(null); }} />
            </div>
            <div>
              <label htmlFor="wellhub-amount" style={{ color: C.gray, display: "block", fontSize: 12, marginBottom: 5 }}>Valor do Wellhub</label>
              <FieldInput id="wellhub-amount" aria-label="Valor do Wellhub" inputMode="decimal" placeholder="0,00" value={wellhubAmount} onChange={event => { setWellhubAmount(event.target.value); setSaveError(null); }} />
            </div>
          </div>
          {saveError && <div role="alert" style={{ color: C.red, fontSize: 12, marginTop: 10 }}>{saveError}</div>}
          {success && <div role="status" style={{ color: C.ink, background: C.limeSoft, borderRadius: 10, padding: 12, fontSize: 12.5, marginTop: 12 }}>{success}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <Button onClick={save} disabled={saving}>{saving ? "Salvando..." : "Salvar configurações"}</Button>
          </div>
        </>
      )}
    </Card>
  );
}
